import { useState, useEffect, useRef } from 'react'
import { ingredientsApi, type Ingredient } from '../services/api'

interface Props {
    value: string
    onChange: (value: string, ingredient?: Ingredient) => void
    placeholder?: string
    className?: string
    compact?: boolean
}

export default function IngredientAutocomplete({ value, onChange, placeholder, className, compact }: Props) {
    const [suggestions, setSuggestions] = useState<Ingredient[]>([])
    const [searching, setSearching] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const query = value.trim()
        if (query.length < 2) {
            setSuggestions([])
            return
        }

        const timer = setTimeout(async () => {
            setSearching(true)
            try {
                const results = await ingredientsApi.search(query)
                setSuggestions(results)
                if (results.length > 0) setShowDropdown(true)
            } catch (err) {
                console.error('Autocomplete error:', err)
            } finally {
                setSearching(false)
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [value])

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div className={`autocomplete-wrapper ${className || ''}`} ref={wrapperRef}>
            <div className="input-icon-wrap" style={{ width: '100%' }}>
                <input
                    className={`input ${compact ? 'input-compact' : ''}`}
                    placeholder={placeholder}
                    value={value}
                    onChange={e => {
                        onChange(e.target.value)
                        if (e.target.value.trim().length < 2) setShowDropdown(false)
                    }}
                    onFocus={() => {
                        if (suggestions.length > 0) setShowDropdown(true)
                    }}
                    style={{ width: '100%' }}
                />
                {searching && (
                    <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="skeleton" style={{ width: 14, height: 14, borderRadius: '50%', opacity: 0.6 }} />
                    </div>
                )}
            </div>
            {showDropdown && suggestions.length > 0 && (
                <div className="autocomplete-dropdown">
                    {suggestions.map(s => (
                        <div
                            key={s.id}
                            className="autocomplete-item"
                            onClick={() => {
                                onChange(s.canonical_name, s)
                                setSuggestions([])
                                setShowDropdown(false)
                            }}
                        >
                            <span>{s.canonical_name}</span>
                            {s.preferred_unit && <span className="autocomplete-item-unit">{s.preferred_unit}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
